import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  Box,
  Flex,
  IconButton,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import {
  ComposedName,
  FieldShell,
  fieldAria,
  fieldIds,
  resolveFieldError,
} from "../FieldShell";

interface SelectProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  style?: React.CSSProperties;
  fieldProps?: Record<string, any>;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  onSelect?: (value: string) => void;
  composedName?: ComposedName;
  required?: boolean;
  hint?: string;
  placeholder?: string;
}

const SelectSearchableComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options = [],
  onSelect,
  composedName,
  itemsError,
  itemsLoading,
  required,
  hint,
  placeholder = "Selecione uma opção",
}: SelectProps<T>) => {
  const error = resolveFieldError(errors, name, composedName);
  const { fieldId } = fieldIds(name);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ),
    [options, searchTerm]
  );

  return (
    <Box style={style}>
      <FieldShell name={name} label={label} required={required} error={error} hint={hint}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selected = options.find((p) => p.value === field.value);
            const canClear = !!field.value && !fieldProps?.disabled;

            if (itemsError) {
              return (
                <Text as="p" size="1" color="red">
                  Erro ao carregar as opções.
                </Text>
              );
            }

            return (
              <Flex align="center" gap="1">
                <Select.Root
                  onValueChange={(value) => {
                    field.onChange(value);
                    onSelect?.(value);
                  }}
                  value={field.value || ""}
                  {...fieldProps}
                >
                  <Select.Trigger
                    id={fieldId}
                    color={error ? "red" : undefined}
                    disabled={itemsLoading || fieldProps?.disabled}
                    {...fieldAria(name, error, hint)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Flex align="center" gap="2">
                      {itemsLoading && <Spinner size="1" />}
                      <Text truncate color={selected ? undefined : "gray"}>
                        {itemsLoading ? "A carregar…" : selected?.label ?? placeholder}
                      </Text>
                    </Flex>
                  </Select.Trigger>

                  <Select.Content position="popper" style={{ maxHeight: "320px" }}>
                    <Box px="2" pt="2" pb="1">
                      <TextField.Root
                        size="1"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Pesquisar…"
                        aria-label={`Pesquisar em ${label}`}
                        // impede que as teclas sejam capturadas pela navegação do Select
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <TextField.Slot>
                          <MagnifyingGlassIcon height="14" width="14" />
                        </TextField.Slot>
                        {searchTerm && (
                          <TextField.Slot>
                            <IconButton
                              type="button"
                              size="1"
                              variant="ghost"
                              color="gray"
                              aria-label="Limpar pesquisa"
                              onClick={() => setSearchTerm("")}
                            >
                              <Cross2Icon height="12" width="12" />
                            </IconButton>
                          </TextField.Slot>
                        )}
                      </TextField.Root>
                    </Box>

                    <Select.Group>
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))
                      ) : (
                        <Box px="3" py="2">
                          <Text size="1" color="gray">
                            Nenhuma opção encontrada
                          </Text>
                        </Box>
                      )}
                    </Select.Group>
                  </Select.Content>
                </Select.Root>

                {canClear && (
                  <Tooltip content="Limpar selecção">
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="gray"
                      size="1"
                      aria-label={`Limpar ${label}`}
                      onClick={() => {
                        field.onChange("");
                        onSelect?.("");
                      }}
                    >
                      <Cross2Icon />
                    </IconButton>
                  </Tooltip>
                )}
              </Flex>
            );
          }}
        />
      </FieldShell>
    </Box>
  );
};

export default SelectSearchableComponent;
